import userflow from 'userflow.js';

import { useEffect, useState } from 'react';

import Icon from 'components/global/Icon';
import Typography from 'components/global/Typography';

import useLocalStorage from 'hooks/useLocalStorage';
import useMediaQuery from 'hooks/useMediaQuery';
import useWindowSize from 'hooks/useWindowSize';

import { Breakpoints } from 'utils/const.styles';

import { faGripDots } from '@fortawesome/pro-regular-svg-icons';
import { faMessagesQuestion } from '@fortawesome/pro-solid-svg-icons';

import * as S from './HelpButton.styled';

const HelpButton = () => {
	const [position, setPosition] = useLocalStorage('helpButtonPosition', window.innerHeight - 150);
	const [isDragging, setIsDragging] = useState(false);
	const [startY, setStartY] = useState(0);
	const [wasDragged, setWasDragged] = useState(false);
	const [initialY, setInitialY] = useState(0);
	const { windowHeight } = useWindowSize();
	const [isIdentified, setIsIdentified] = useState(userflow.isIdentified());
	const dragThreshold = 5;
	const isMobile = useMediaQuery(Breakpoints.mobileLandscape);

	const [announcementCount, setAnnouncementCount] = useState(
		userflow.getResourceCenterState()?.unreadAnnouncementCount || 0
	);

	const handleAnnouncementSeen = () => {
		setAnnouncementCount(userflow.getResourceCenterState()?.unreadAnnouncementCount || 0);
	};

	useEffect(() => {
		userflow.on('resourceCenterChanged', handleAnnouncementSeen);

		return () => {
			userflow.off('resourceCenterChanged', handleAnnouncementSeen);
		};
	}, [isIdentified]);

	const handleUserflowIdentify = () => {
		setIsIdentified(true);
		handleAnnouncementSeen();
	};

	const handleUserflowReset = () => {
		setIsIdentified(false);
		handleAnnouncementSeen();
	};

	useEffect(() => {
		window.addEventListener('userflow:identify', handleUserflowIdentify);
		window.addEventListener('userflow:reset', handleUserflowReset);

		return () => {
			window.removeEventListener('userflow:identify', handleUserflowIdentify);
			window.removeEventListener('userflow:reset', handleUserflowReset);
		};
	}, []);

	useEffect(() => {
		// validate new window size to avoid overflow
		if (position < 90) {
			setPosition(90);
		} else if (position > window.innerHeight - 150) {
			setPosition(window.innerHeight - 150);
		}
	}, [windowHeight]);

	const handleDrag = (clientY) => {
		const deltaY = clientY - initialY;
		if (Math.abs(deltaY) > dragThreshold) {
			let newY = clientY - startY;
			newY = Math.max(90, Math.min(newY, window.innerHeight - 150));
			setPosition(newY);
			setWasDragged(true);
		}
	};

	const handleStartDrag = (clientY) => {
		setIsDragging(true);
		setStartY(clientY - position);
		setInitialY(clientY);
		setWasDragged(false);

		// Disable touch scrolling
		document.body.style.overflow = 'hidden';
	};

	const handleEndDrag = () => {
		setIsDragging(false);

		// Re-enable touch scrolling
		document.body.style.overflow = '';
	};

	useEffect(() => {
		const handleMouseMove = (e) => isDragging && handleDrag(e.clientY);
		const handleTouchMove = (e) => {
			if (isDragging) {
				e.preventDefault();
				handleDrag(e.touches[0].clientY);
			}
		};

		const eventType = isDragging ? 'addEventListener' : 'removeEventListener';
		document[eventType]('mousemove', handleMouseMove);
		document[eventType]('mouseup', handleEndDrag);
		document[eventType]('touchmove', handleTouchMove);
		document[eventType]('touchend', handleEndDrag);

		return () => {
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', handleEndDrag);
			document.removeEventListener('touchmove', handleTouchMove);
			document.removeEventListener('touchend', handleEndDrag);
		};
	}, [isDragging]);

	const handleClick = () => {
		if (!wasDragged) {
			userflow.openResourceCenter();
		}
	};

	return (
		isIdentified && (
			<S.HelpButton
				onClick={handleClick}
				onMouseDown={(e) => handleStartDrag(e.clientY)}
				onTouchStart={(e) => handleStartDrag(e.touches[0].clientY)}
				style={{ top: `${position}px`, cursor: isDragging && wasDragged ? 'move' : 'pointer' }}>
				<S.Content>
					<S.LightIcon icon={faGripDots} color="var(--alwaysWhiteColor)" size="medium" className="dragIcon" />
					{!isMobile && (
						<Typography variant="p3" color="var(--alwaysWhiteColor)" fontWeight="500">
							help
						</Typography>
					)}
					<Icon icon={faMessagesQuestion} color="var(--alwaysWhiteColor)" size="medium" />
				</S.Content>
				{announcementCount > 0 && (
					<S.NotificationBadge>
						<Typography variant="p3" color="var(--alwaysWhiteColor)" fontWeight="500">
							{announcementCount}
						</Typography>
					</S.NotificationBadge>
				)}
			</S.HelpButton>
		)
	);
};

export default HelpButton;
